import { useEffect, useRef, useState } from 'react';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Typography from '@mui/material/Typography';

import { listBillingProviders } from '../../api/claimsClient';

/** Display name mirrors ProvidersPage.providerDisplayName. */
function providerDisplayName(profile) {
  if (!profile) return '';
  return (
    profile.organization_name ||
    [profile.first_name, profile.last_name].filter(Boolean).join(' ') ||
    'Unnamed provider'
  );
}

function npiLast4(npi) {
  const s = (npi || '').toString();
  return s ? `••••${s.slice(-4)}` : '—';
}

/**
 * Slice C1 — read-only current billing-provider tie + "Change" dropdown.
 *
 * Props:
 *  - currentProfile: { id, organization_name, first_name, last_name, npi } | null
 *  - disabled: boolean (locked tickets cannot change the tie)
 *  - onSave: (profileId) => Promise  (parent persists + refetches the claim)
 */
export default function BillingProviderPicker({ currentProfile, disabled, onSave }) {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState(null); // null = not yet fetched
  const [loadingList, setLoadingList] = useState(false);
  const [listError, setListError] = useState(null);
  const [selectedId, setSelectedId] = useState(currentProfile?.id || '');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Keep latest onSave in a ref so effects never depend on a parent callback.
  const onSaveRef = useRef(onSave);
  onSaveRef.current = onSave;

  // Lazily fetch the provider list when the picker is revealed. Keyed on the
  // stable `open` boolean, not on a parent callback.
  useEffect(() => {
    if (!open || options !== null) return undefined;
    let cancelled = false;
    setLoadingList(true);
    setListError(null);
    listBillingProviders()
      .then((rows) => {
        if (cancelled) return;
        setOptions(Array.isArray(rows) ? rows : []);
      })
      .catch(() => {
        if (!cancelled) setListError('Could not load billing providers.');
      })
      .finally(() => {
        if (!cancelled) setLoadingList(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, options]);

  const handleChangeClick = () => {
    setSelectedId(currentProfile?.id || '');
    setSaveError(null);
    setOpen(true);
  };

  const handleCancel = () => {
    setOpen(false);
    setSaveError(null);
  };

  const handleSave = async () => {
    if (!selectedId || saving) return;
    setSaving(true);
    setSaveError(null);
    try {
      await onSaveRef.current(selectedId);
      setOpen(false);
    } catch (_e) {
      setSaveError('Could not save the billing provider. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ mt: 2 }} data-testid="billing-provider-picker">
      <Typography variant="caption" color="text.secondary" display="block">
        Billing Provider
      </Typography>

      {currentProfile ? (
        <Typography variant="body2" data-testid="bpp-current">
          {providerDisplayName(currentProfile)}{' '}
          <Typography component="span" variant="body2" color="text.secondary">
            NPI {npiLast4(currentProfile.npi)}
          </Typography>
        </Typography>
      ) : (
        <Typography variant="body2" color="warning.main" data-testid="bpp-none">
          No billing provider tied
        </Typography>
      )}

      {!open && (
        <Button
          size="small"
          variant="text"
          onClick={handleChangeClick}
          disabled={disabled}
          sx={{ mt: 0.5, px: 0 }}
        >
          Change billing provider
        </Button>
      )}

      {open && (
        <Box sx={{ mt: 1 }}>
          {loadingList ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">
                Loading providers…
              </Typography>
            </Box>
          ) : listError ? (
            <Alert severity="error" sx={{ mb: 1 }}>{listError}</Alert>
          ) : (
            <FormControl size="small" fullWidth sx={{ maxWidth: 420 }}>
              <Select
                value={selectedId}
                onChange={(e) => setSelectedId(e.target.value)}
                displayEmpty
                inputProps={{ 'aria-label': 'Billing provider' }}
              >
                <MenuItem value="" disabled>
                  Select a billing provider…
                </MenuItem>
                {(options || []).map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {providerDisplayName(p)} — NPI {npiLast4(p.npi)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {saveError && <Alert severity="error" sx={{ mt: 1 }}>{saveError}</Alert>}

          <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
            <Button
              size="small"
              variant="contained"
              onClick={handleSave}
              disabled={!selectedId || saving || loadingList}
            >
              {saving ? 'Saving…' : 'Save'}
            </Button>
            <Button size="small" variant="text" onClick={handleCancel} disabled={saving}>
              Cancel
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
}
